<template>
  <div
    class="ndsTooltip"
    @mouseleave="handleHideEvent"
  >
    <div
      ref="tooltipTrigger"
      class="ndsTooltip__trigger"
      :class="{ '-enabled': enabled && hasHelpCursor }"
      data-testid="nds-tooltip-trigger"
      @mouseenter="handleShowEvent"
    >
      <slot>
        <NdsIcon
          class="ndsTooltip__icon"
          :icon="NdsIconType.INFO"
          :size="16"
        />
      </slot>
    </div>

    <div
      ref="tooltipWrapper"
      class="ndsTooltip__wrapper"
      :class="{ '-show': show }"
      data-testid="nds-tooltip-wrapper"
      @click.stop
    >
      <NdsText
        v-if="title"
        class="ndsTooltip__title"
        data-testid="nds-tooltip-title"
        emphasis
        :size="NdsTextSizes.S"
      >
        <slot name="title">
          {{ title }}
        </slot>
      </NdsText>

      <NdsText
        class="ndsTooltip__content"
        data-testid="nds-tooltip-content"
        :size="NdsTextSizes.S"
      >
        <slot name="content">
          {{ content }}
        </slot>
      </NdsText>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, type PropType } from 'vue'
import Popper, { type PopperOptions } from 'popper.js'

import NdsText from '@/components/NewDesignSystem/NdsText/NdsText.vue'
import NdsIcon from '@/components/NewDesignSystem/NdsIcon/NdsIcon.vue'

import { NdsIconType } from '@/components/NewDesignSystem/NdsIcon/NdsIcon.interfaces'
import { NdsTextSizes } from '@/components/NewDesignSystem/NdsText/NdsText.interfaces'
import { NdsTooltipPosition } from '@/components/NewDesignSystem/NdsTooltip/NdsTooltip.interfaces'

const props = defineProps({
  enabled: {
    type: Boolean,
    default: true,
  },
  title: {
    type: String,
    default: '',
  },
  content: {
    type: String,
  },
  position: {
    type: String as PropType<NdsTooltipPosition>,
    default: NdsTooltipPosition.AUTO,
  },
  hasHelpCursor: {
    type: Boolean,
    default: true,
  },
})

const show = ref(false)
const popper = ref<Popper>()
const options = ref<PopperOptions>({
  positionFixed: true,
  placement: props.position,
  modifiers: {
    flip: { enabled: true },
    preventOverflow: {
      enabled: true,
      boundariesElement: 'viewport',
    },
  },
})
const tooltipWrapper = ref<Element>()
const tooltipTrigger = ref<Element>()

const handleShowEvent = () => {
  if (props.enabled) {
    show.value = true

    if (tooltipTrigger.value && tooltipWrapper.value) {
      popper.value = new Popper(tooltipTrigger.value, tooltipWrapper.value, options.value)
    }
  }
}

const handleHideEvent = () => {
  if (show.value) {
    show.value = false

    if (popper.value) {
      popper.value.destroy()
    }
  }
}
</script>

<style lang="scss" scoped>
.ndsTooltip {
  &__wrapper {
    position: fixed;
    width: max-content;
    max-width: 320px;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-1);
    padding: var(--spacing-1-5);
    background-color: var(--neutral-950);
    border-radius: var(--spacing-0-5);
    z-index: var(--z-index-popover);
    pointer-events: none;
    opacity: 0;
    cursor: default;

    * {
      color: var(--neutral-50);
    }

    &.-show {
      pointer-events: all;
      transition: opacity ease-in 120ms;
      transition-delay: 250ms;
      opacity: 1;
    }
  }

  &__trigger.-enabled {
    cursor: help;
  }
}
</style>
